import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { getPlanInfoFromBillingType } from '@mm/state/helpers/billing-info-helpers';
import {
  SubscriptionTabDefinition,
  TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE,
} from '@mm/state/helpers/billing-subscription-tab-helpers';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import {
  EnhancedPlan,
  PlanBillingPeriodBE,
} from '@mm/state/interfaces/subscription';
import {
  isBilling2023Subscription,
  isBilling2025Subscription,
  isBilling2025V2Subscription,
} from '@mm/state/model/SubscriptionModel';
import {
  OpenBillingModalParams,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import { enhancedBillingPagesSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  defaultTeamPlanSelector,
  getCurrentSubscription,
} from '@mm/state/selectors/subscriptionSelectors';
import { companyAdminSelector } from '@mm/state/selectors/viewSelectors';
import { centsToDollars } from '@mm/utils';
import * as classnames from 'classnames';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BillingPannelContext } from '../../../BillingPanel';
import { BillingPageMetadataCollapser } from '../BillingPageCard/BillingPageMetadata/BillingPageMetadataCollapser';
import { BillingPageTable } from '../BillingPageCard/BillingPageTable/BillingPageTable';
import { BillingUserLimit } from '../BillingPageCard/BillingUserLimit/BillingUserLimit';
import { BudgetCap } from '../BillingPageCard/BudgetCap/BudgetCap';
import { getShowTeamOverageArea } from '../helpers';
import { TeamOveragesArea } from '../TeamOveragesArea/TeamOveragesArea';
import { TeamPlanButtonsArea } from '../TeamPlanButtonsArea/TeamPlanButtonsArea';
import './ActivePlanDetails.pcss';

type Props = {
  planTab: SubscriptionTabDefinition;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  loadPricesForNextLevel: (subscriptionId: number, level: number) => void;
};

const renderTeamUpgradeButton = (
  teamTrial: boolean | undefined,
  isCompanyAdmin: boolean,
  defaultTeamPlan: EnhancedPlan | undefined,
  openBillingModal: (props: Partial<OpenBillingModalParams>) => void,
  landmineOpenId: number | undefined,
) => {
  if (!(teamTrial && isCompanyAdmin && defaultTeamPlan)) {
    return null;
  }
  return (
    <PrimaryButton
      className={classnames('billing-panel__upgrade-team-button', {
        'billing-panel__upgrade-team-trial-button': teamTrial,
      })}
      onClick={() =>
        openBillingModal({
          plan: defaultTeamPlan,
          landmineOpenId,
          stayOnPage: false,
        })
      }
    >
      Upgrade
    </PrimaryButton>
  );
};
// tslint:disable-next-line:cyclomatic-complexity
export const ActivePlanDetails = (props: Props) => {
  const { planTab, setSearchInput, searchInput, loadPricesForNextLevel } =
    props;

  const {
    tabDetails: { teamMessageStatistics },
    shortName,
    teamSubscription,
    plan,
    pages,
    planCode,
  } = planTab;

  const defaultTeamPlan = useSelector(defaultTeamPlanSelector);
  const isCompanyAdmin = useSelector(companyAdminSelector);
  const connectedPages = useSelector(enhancedBillingPagesSelector);

  const facebookPageId = connectedPages.find(
    p => p.subscription?.id === teamSubscription?.id,
  )?.id;
  const facebookPage = connectedPages.find(p => p.id === facebookPageId);
  const currentSubscription = useSelector(
    getCurrentSubscription(facebookPageId),
  );
  const pageBillingType = plan ? plan.pageBillingType : PageBillingType.Free;

  const { landmineOpenId } = useContext(BillingPannelContext);
  const { getCurrentSubscription: retrieveCurrentSubscription } =
    useSubscriptionCRUDActions();
  const { openBillingModal } = useModalActions();

  const planInfoMemoised = useMemo(
    () => getPlanInfoFromBillingType(pageBillingType),
    [pageBillingType],
  );

  const billingPeriod =
    (plan && plan.billingPeriod) || PlanBillingPeriodBE.month;
  const pageWithTrial = pages.find(p => p.redemption && p.redemption.teamTrial);
  const redemption = pageWithTrial && pageWithTrial.redemption;
  const teamTrial = planCode === TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE;
  const isBillign2025Subscription =
    facebookPage?.subscription &&
    isBilling2025Subscription(facebookPage.subscription);
  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage.subscription);

  const showTeamOverageArea =
    !isNewBillingSubscription &&
    getShowTeamOverageArea(teamMessageStatistics, teamSubscription);

  const planDetailsName = classnames('active-plan-details', {
    'active-plan-details--trial': teamTrial,
  });
  const trialPeriod = redemption
    ? Math.max(-moment().diff(redemption.expiresAt, 'days'), 0)
    : 0;

  const retrieveSubscription = () => {
    if (facebookPageId) {
      retrieveCurrentSubscription(
        facebookPageId,
        false,
        facebookPage?.subscription?.id,
        isNewBillingSubscription,
      );
    }
  };

  useEffect(() => {
    if (!currentSubscription) {
      retrieveSubscription();
    }
  }, [currentSubscription, facebookPageId]);

  const {
    isAnyTeamPlan,
    isLegacyPlan,
    isCustomTeam,
    isCustomPro,
    isLegacyTeamPlan,
    isWordPress,
  } = planInfoMemoised;

  const showPriceAndAllotment =
    isLegacyPlan || isCustomTeam || isCustomPro || isWordPress;

  const showOveragesArea =
    isCustomTeam || isLegacyTeamPlan
      ? teamSubscription &&
        !isEmpty(teamSubscription.smsNumbers) &&
        showTeamOverageArea
      : teamSubscription && showTeamOverageArea;
  const currentSubPrice = centsToDollars(
    currentSubscription?.currentInvoiceAmount?.subsPrice || 0,
  );

  const isESPBilling =
    currentSubscription && isBilling2025V2Subscription(currentSubscription);

  return (
    <div
      className={
        isAnyTeamPlan ? planDetailsName : 'active-plan-details__non-team'
      }
    >
      {isAnyTeamPlan && (
        <div className="active-plan-details__header-top">
          <div className="active-plan-details__name-wrapper">
            <div className="active-plan-details__plan-name">{shortName}</div>

            {showPriceAndAllotment && currentSubPrice && !isESPBilling && (
              <div className="active-plan-details__price">
                ${currentSubPrice}/
                <span className="active-plan-details__price-billing-period">
                  {(currentSubscription?.planIntervalLength || 0) > 1 &&
                    currentSubscription?.planIntervalLength}{' '}
                  {billingPeriod}
                </span>
              </div>
            )}
          </div>

          {renderTeamUpgradeButton(
            teamTrial,
            isCompanyAdmin,
            defaultTeamPlan,
            openBillingModal,
            landmineOpenId,
          )}

          {teamSubscription && plan && (
            <TeamPlanButtonsArea
              teamSubscription={teamSubscription}
              pages={pages}
              plan={plan}
              setSearchInput={setSearchInput}
              searchInput={searchInput}
              facebookPageId={facebookPageId}
              planTab={planTab}
            />
          )}
        </div>
      )}

      {teamTrial && (
        <div className="active-plan-details__team-trial-countdown">
          <div className="active-plan-details__team-trial-countdown-label">
            Trial period:{' '}
            <span style={{ fontWeight: 500 }}>
              {trialPeriod} days remaining
            </span>
          </div>
        </div>
      )}

      <div className="active-plan-details__body">
        {planInfoMemoised.isLegacyPlan && (
          <div className="active-plan-details__legacy-plan-text">
            Learn more about your legacy plan
            <a
              className="active-plan-details__legacy-plan-link"
              target="_blank"
              rel="noopener noreferrer"
              href="https://mobilemonkey.com/help/help/article/d4f8r99li9-mobile-monkey-legacy-subscriptions"
            >
              here
            </a>
          </div>
        )}
        {planInfoMemoised.isAnyTeamPlan && (
          <>
            <BillingPageTable
              page={facebookPage || pages[0]}
              planTab={planTab}
              planInfoMemoised={planInfoMemoised}
              loadPricesForNextLevel={loadPricesForNextLevel}
            />

            {isBillign2025Subscription && currentSubscription && (
              <BudgetCap
                subscription={currentSubscription}
                retrieveSubscription={retrieveSubscription}
                page={facebookPage}
              />
            )}
            {isNewBillingSubscription && currentSubscription && (
              <BillingUserLimit
                limitType="subscription"
                subscription={currentSubscription}
                facebookPageId={facebookPage.id}
                retrieveSubscription={retrieveSubscription}
                withOvergaes
              />
            )}

            <BillingPageMetadataCollapser
              page={facebookPage || pages[0]}
              planTab={planTab}
            />
          </>
        )}
      </div>

      {teamSubscription && showOveragesArea && (
        <TeamOveragesArea
          subscription={teamSubscription}
          planInfo={planInfoMemoised}
        />
      )}
    </div>
  );
};
